import {
  Contact,
  DocumentAnalysis,
  DocumentAnalysisStatus,
  Entity,
  TaskCategory,
} from '@admiin-com/ds-graphql';
import { useEntityId } from '../../../features/hooks/useEntityContext/useEntityContext';
import { useDocumentAnalysis } from '../../../hooks/useDocumentAnalysis/useDocumentAnalysis';
import React from 'react';
import { Bill } from '../PayBillCreateForm';
import { useDefaultFrom } from './useDefaultFrom';
import { transformDocumentAnalysisToBill } from '../Upload/transformer';
import { frontDateFromISO } from '@admiin-com/ds-common';
import { getFileNameFromKey } from '@admiin-com/ds-amplify-web';
import { useDefaultValueForFromTo } from '../../../features/providers/TaskCreationProvider/TaskCreationProvider';

export const useSetPreviousScans = ({
  appendBill,
}: {
  appendBill: (bill: Bill) => void;
}) => {
  const defaultFrom = useDefaultFrom();
  const entityId = useEntityId();
  const entity = useDefaultValueForFromTo();
  // Use the new hook for SCANNED documents
  const {
    documentAnalysisData: scannedDocuments,
    handleLoadMore: loadMoreScanned,
    hasNextPage: hasMoreScanned,
    loading: loadingScanned,
  } = useDocumentAnalysis({
    entityId,
    limit: 5,
    status: DocumentAnalysisStatus.SCANNED,
    category: TaskCategory.OTHER,
  });
  const {
    documentAnalysisData: inProgressDocuments,
    handleLoadMore: loadMoreInProgress,
    hasNextPage: hasNextPageInProgress,
    loading: loadingInProgress,
  } = useDocumentAnalysis({
    entityId,
    limit: 5,
    status: DocumentAnalysisStatus.IN_PROGRESS,
    category: TaskCategory.OTHER,
  });

  const documentAnalysisData = React.useMemo(
    () => [...scannedDocuments, ...inProgressDocuments],
    [scannedDocuments, inProgressDocuments]
  );

  const hasMoreData = hasMoreScanned || hasNextPageInProgress;

  React.useEffect(() => {
    for (const item of documentAnalysisData) {
      appendBill({
        ...transformDocumentAnalysisToBill(item, defaultFrom),
        to: entity,
        documentName: getFileNameFromKey(item.documents?.[0]?.key ?? ''),
        documentSize: frontDateFromISO(item.createdAt ?? ''),
        documentAnalysis: item,
      });
    }
  }, [documentAnalysisData, defaultFrom, entity]);

  const loadMore = () => {
    if (hasMoreScanned) {
      loadMoreScanned();
    }
    if (hasNextPageInProgress) {
      loadMoreInProgress();
    }
  };
  return {
    hasMoreData,
    loadMore,
    loading: loadingInProgress || loadingScanned,
  };
};
